/**
 * User Status Reference Component
 * Displays comprehensive documentation for user status types
 */
import {
  faBan,
  faCheckCircle,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faInfoCircle,
  faPauseCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

export function UserStatusReference() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className='mb-6 rounded-lg border-2 border-blue-300 bg-blue-50 shadow-md dark:border-blue-700 dark:bg-blue-900/10'>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className='flex w-full items-center justify-between p-4 text-left transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/20'
      >
        <div className='flex items-center gap-3'>
          <FontAwesomeIcon
            icon={faInfoCircle}
            className='text-xl text-blue-600 dark:text-blue-400'
          />
          <div>
            <h3 className='text-lg font-semibold text-blue-800 dark:text-blue-200'>
              User Status Reference
            </h3>
            <p className='text-sm text-blue-600 dark:text-blue-400'>
              Click to view detailed information about user status types
            </p>
          </div>
        </div>
        <FontAwesomeIcon
          icon={isExpanded ? faChevronUp : faChevronDown}
          className='text-blue-800 dark:text-blue-200'
        />
      </button>

      {isExpanded && (
        <div className='space-y-6 border-t-2 border-blue-300 bg-blue-50 p-6 dark:border-blue-700 dark:bg-blue-900/10'>
          {/* ACTIVE */}
          <div className='rounded-lg border border-green-300 bg-green-50 p-4 dark:border-green-700 dark:bg-green-900/10'>
            <div className='mb-3 flex items-center gap-2'>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className='text-xl text-green-600 dark:text-green-400'
              />
              <h4 className='text-lg font-semibold text-green-800 dark:text-green-200'>ACTIVE</h4>
            </div>
            <p className='mb-2 text-sm text-green-700 dark:text-green-300'>
              <strong>Description:</strong> Normal operational status with full service access.
            </p>
            <div className='mb-2 text-sm text-green-700 dark:text-green-300'>
              <strong>Behavior:</strong>
              <ul className='mt-1 ml-4 list-disc'>
                <li>Full access to all features and services</li>
                <li>Can create and manage scheduled posts</li>
                <li>OAuth sessions remain valid</li>
                <li>No restrictions applied</li>
              </ul>
            </div>
            <div className='text-sm text-green-700 dark:text-green-300'>
              <strong>Use Cases:</strong> Default for new users, status after suspension lifted
            </div>
          </div>

          {/* RESTRICTED */}
          <div className='rounded-lg border border-orange-300 bg-orange-50 p-4 dark:border-orange-700 dark:bg-orange-900/10'>
            <div className='mb-3 flex items-center gap-2'>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className='text-xl text-orange-600 dark:text-orange-400'
              />
              <h4 className='text-lg font-semibold text-orange-800 dark:text-orange-200'>
                RESTRICTED
              </h4>
            </div>
            <p className='mb-2 text-sm text-orange-700 dark:text-orange-300'>
              <strong>Description:</strong> Limited access with specific feature restrictions.
            </p>
            <div className='mb-2 text-sm text-orange-700 dark:text-orange-300'>
              <strong>Behavior:</strong>
              <ul className='mt-1 ml-4 list-disc'>
                <li>User can log in and access the service</li>
                <li>OAuth sessions remain valid</li>
                <li>Plan-based restrictions may apply</li>
                <li>Specific features may be limited</li>
              </ul>
            </div>
            <div className='text-sm text-orange-700 dark:text-orange-300'>
              <strong>Use Cases:</strong> Soft enforcement, gradual warning before suspension
            </div>
          </div>

          {/* SUSPENDED */}
          <div className='rounded-lg border border-yellow-300 bg-yellow-50 p-4 dark:border-yellow-700 dark:bg-yellow-900/10'>
            <div className='mb-3 flex items-center gap-2'>
              <FontAwesomeIcon
                icon={faPauseCircle}
                className='text-xl text-yellow-600 dark:text-yellow-400'
              />
              <h4 className='text-lg font-semibold text-yellow-800 dark:text-yellow-200'>
                SUSPENDED
              </h4>
            </div>
            <p className='mb-2 text-sm text-yellow-700 dark:text-yellow-300'>
              <strong>Description:</strong> Temporary account suspension with automatic or manual
              lifting.
            </p>
            <div className='mb-2 text-sm text-yellow-700 dark:text-yellow-300'>
              <strong>Behavior:</strong>
              <ul className='mt-1 ml-4 list-disc'>
                <li>
                  <strong>Immediate:</strong> User logged out on next /auth/me check
                </li>
                <li>
                  <strong>Session:</strong> Returns 403 FORBIDDEN error
                </li>
                <li>
                  <strong>Frontend:</strong> Auto-logout with suspension message
                </li>
                <li>
                  <strong>Reservations:</strong> All pending scheduled posts and scheduled actions
                  are cancelled, and are NOT restored on lift
                </li>
                <li>
                  <strong>Temporary:</strong> Auto-lifted when suspended_until passes (hourly at
                  :10)
                </li>
                <li>
                  <strong>Permanent:</strong> suspended_until is NULL, requires manual lift
                </li>
              </ul>
            </div>
            <div className='mb-2 text-sm text-yellow-700 dark:text-yellow-300'>
              <strong>Use Cases:</strong> ToS violations, cooling-off period, investigation
            </div>
            <div className='mb-2 rounded bg-yellow-100 p-2 text-xs text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'>
              <strong>⚠️ Irreversible:</strong> Cancelling reservations is destructive. Lifting the
              suspension does not bring them back, and reservations whose scheduled time has already
              passed stay cancelled. This matches the Terms of Service.
            </div>
            <div className='rounded bg-yellow-100 p-2 text-xs text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'>
              <strong>⚠️ Auto-Lift Delay:</strong> Scheduler runs hourly at minute 10 (max ~1h 10min
              delay). All datetime comparisons use UTC timezone.
            </div>
          </div>

          {/* BLOCKED */}
          <div className='rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/10'>
            <div className='mb-3 flex items-center gap-2'>
              <FontAwesomeIcon icon={faBan} className='text-xl text-red-600 dark:text-red-400' />
              <h4 className='text-lg font-semibold text-red-800 dark:text-red-200'>BLOCKED</h4>
            </div>
            <p className='mb-2 text-sm text-red-700 dark:text-red-300'>
              <strong>Description:</strong> Severe restriction, typically permanent.
            </p>
            <div className='mb-2 text-sm text-red-700 dark:text-red-300'>
              <strong>Behavior:</strong>
              <ul className='mt-1 ml-4 list-disc'>
                <li>
                  <strong>Identical to SUSPENDED</strong> in enforcement
                </li>
                <li>User logged out on next /auth/me check</li>
                <li>Returns 403 FORBIDDEN error</li>
                <li>
                  All pending scheduled posts and scheduled actions are cancelled, and are NOT
                  restored on lift
                </li>
                <li>Semantic difference: more severe than SUSPENDED</li>
                <li>Supports temporary blocking with suspended_until</li>
              </ul>
            </div>
            <div className='mb-2 text-sm text-red-700 dark:text-red-300'>
              <strong>Use Cases:</strong> Severe ToS violations, spam, security incidents
            </div>
            <div className='text-sm text-red-700 dark:text-red-300'>
              <strong>Distinction:</strong> More severe meaning, often permanent, escalation from
              SUSPENDED
            </div>
          </div>

          {/* Best Practices */}
          <div className='rounded-lg border border-gray-300 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800'>
            <h4 className='mb-3 text-lg font-semibold text-gray-800 dark:text-gray-200'>
              Best Practices
            </h4>
            <div className='space-y-2 text-sm text-gray-700 dark:text-gray-300'>
              <div>
                <strong>Choosing Status:</strong>
                <ol className='mt-1 ml-4 list-decimal'>
                  <li>ACTIVE: Default for all users</li>
                  <li>RESTRICTED: First warning, specific feature limits</li>
                  <li>SUSPENDED: Temporary enforcement, investigation</li>
                  <li>BLOCKED: Severe violations, typically permanent</li>
                </ol>
              </div>
              <div>
                <strong>Suspension Guidelines:</strong>
                <ul className='mt-1 ml-4 list-disc'>
                  <li>Always provide clear reason (visible to users)</li>
                  <li>Use temporary for first offenses</li>
                  <li>Escalate to BLOCKED for repeated violations</li>
                  <li>Review status history before action</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Status Change Logging */}
          <div className='rounded-lg border border-indigo-300 bg-indigo-50 p-4 dark:border-indigo-700 dark:bg-indigo-900/10'>
            <h4 className='mb-3 text-lg font-semibold text-indigo-800 dark:text-indigo-200'>
              Status Change Logging
            </h4>
            <div className='space-y-2 text-sm text-indigo-700 dark:text-indigo-300'>
              <p>All status changes are automatically logged with:</p>
              <ul className='ml-4 list-disc'>
                <li>Action type (SUSPEND, BLOCK, LIFT, AUTO_LIFT, RESTRICT, ACTIVATE)</li>
                <li>Previous and new status</li>
                <li>Reason (if provided)</li>
                <li>Performer (ADMIN, SYSTEM, USER)</li>
                <li>Timestamp and metadata</li>
              </ul>
              <p className='mt-2'>
                <strong>Access:</strong> Click "View Status History" button to see full log in new
                tab.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
